import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Calculator, Sparkles } from 'lucide-react';
import { InlineAddProjectType, InlineAddOnsList } from './InlineLibraryEditors';
import { suggestMarkup } from './pricingIntelligence';

const QuoteCalculator = ({
  customerName,
  setCustomerName,
  selectedTemplate,
  setSelectedTemplate,
  projectType,
  setProjectType,
  squareFeet,
  setSquareFeet,
  linearFeet,
  setLinearFeet,
  hours,
  setHours,
  count,
  setCount,
  laborRate,
  setLaborRate,
  markup,
  setMarkup,
  customMaterialRate,
  setCustomMaterialRate,
  selectedAddOns,
  toggleAddOn,
  projectTypes,
  setProjectTypes,
  addOns,
  setAddOns,
  getProjectTypeData,
  refreshLibrary,
  quote,
  user,
  TEMPLATES
}) => {
  const projectData = getProjectTypeData();
  const [optimizedHint, setOptimizedHint] = useState(null);

  const handleAutoOptimize = () => {
    const subtotal = parseFloat(quote?.subtotal) || 0;
    const result = suggestMarkup(projectType, subtotal);
    setMarkup(String(result.percent));
    setOptimizedHint(result);
    // Auto-clear hint after a few seconds so the UI doesn't get crowded.
    setTimeout(() => setOptimizedHint(null), 6000);
  };
  
  return (
    <Card className="border-2 border-slate-200 shadow-xl">
      <CardHeader className="bg-gradient-to-r from-blue-50 to-slate-50 border-b">
        <CardTitle className="flex items-center gap-2 text-slate-900">
          <Calculator className="w-5 h-5 text-blue-600" />
          Quote Calculator
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-6">
        {/* Customer Name */}
        <div className="space-y-2">
          <Label htmlFor="customer-name">Customer Name (Optional)</Label>
          <Input 
            id="customer-name"
            placeholder="John Smith"
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
          />
        </div>

        {/* Template Selector */}
        <div className="space-y-2">
          <Label htmlFor="quote-template-trigger">Quote Template</Label>
          <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
            <SelectTrigger id="quote-template-trigger" aria-label="Quote template">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(TEMPLATES).map(([key, template]) => (
                <SelectItem 
                  key={key} 
                  value={key}
                  disabled={template.tier === 'pro' && user?.subscription_status !== 'pro'}
                >
                  <div className="flex items-center justify-between w-full">
                    <span>{template.name}</span>
                    {template.tier === 'pro' && user?.subscription_status !== 'pro' && (
                      <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Pro</span>
                    )}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {TEMPLATES[selectedTemplate]?.tier === 'pro' && user?.subscription_status !== 'pro' && (
            <p className="text-xs text-amber-600">
              🔒 Pro template - Upgrade to unlock all templates
            </p>
          )}
        </div>

        {/* Project Type */}
        <div className="space-y-2">
          <Label htmlFor="quote-project-type-select">Project Type</Label>
          <Select value={projectType} onValueChange={setProjectType}>
            <SelectTrigger id="quote-project-type-select" aria-label="Project type" data-testid="quote-project-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {projectTypes.map(pt => (
                <SelectItem key={pt.value} value={pt.value} data-testid={`quote-project-type-option-${pt.value}`}>
                  {pt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <InlineAddProjectType
            user={user}
            refreshLibrary={refreshLibrary}
            setProjectTypes={setProjectTypes}
          />
        </div>

        {/* Measurement Input - Dynamic based on project type */}
        {projectData.measurementType === 'linear_feet' && (
          <div className="space-y-2">
            <Label htmlFor="linear-feet">Linear Feet</Label>
            <Input 
              id="linear-feet"
              type="number"
              placeholder="20"
              value={linearFeet}
              onChange={(e) => setLinearFeet(e.target.value)}
            />
          </div>
        )}
        
        {projectData.measurementType === 'hourly' && (
          <div className="space-y-2">
            <Label htmlFor="hours">Estimated Hours</Label>
            <Input 
              id="hours"
              type="number"
              placeholder="4"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
            />
            <p className="text-xs text-slate-500">
              Estimated time to complete the job
            </p>
          </div>
        )}
        
        {projectData.measurementType === 'count' && (
          <div className="space-y-2">
            <Label htmlFor="count">
              Number of {
                projectData.label === 'Window Installation' ? 'Windows' :
                projectData.label === 'Lawn Care' ? 'Yards / Visits' :
                'Units'
              }
            </Label>
            <Input 
              id="count"
              type="number"
              placeholder="5"
              value={count}
              onChange={(e) => setCount(e.target.value)}
            />
          </div>
        )}
        
        {projectData.measurementType === 'square_feet' && (
          <div className="space-y-2">
            <Label htmlFor="square-feet">Square Footage</Label>
            <Input 
              id="square-feet"
              type="number"
              placeholder="500"
              value={squareFeet}
              onChange={(e) => setSquareFeet(e.target.value)}
            />
          </div>
        )}

        {/* Labor Rate */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="labor-rate">Labor Rate ($/hr)</Label>
            <Input 
              id="labor-rate"
              type="number"
              placeholder="50"
              value={laborRate}
              onChange={(e) => setLaborRate(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="markup">Markup (%)</Label>
              <button
                type="button"
                onClick={handleAutoOptimize}
                className="inline-flex items-center gap-1 text-xs font-semibold text-purple-700 hover:text-purple-800"
                data-testid="markup-auto-optimize-btn"
              >
                <Sparkles className="w-3.5 h-3.5" />
                Auto-optimize
              </button>
            </div>
            <Input 
              id="markup"
              type="number"
              placeholder="20"
              value={markup}
              onChange={(e) => setMarkup(e.target.value)}
              data-testid="markup-input"
            />
            {optimizedHint && (
              <div
                className="text-xs bg-purple-50 border border-purple-200 text-purple-800 rounded-md px-2 py-1.5"
                data-testid="markup-optimized-hint"
              >
                <strong>{optimizedHint.percent}%</strong> — {optimizedHint.rationale}
              </div>
            )}
          </div>
        </div>

        {/* Custom Material Rate - Context-aware label */}
        <div className="space-y-2">
          <Label htmlFor="material-rate">
            Custom Material Rate 
            {projectData.measurementType === 'hourly' && ' ($/hr)'}
            {projectData.measurementType === 'square_feet' && ' ($/sqft)'}
            {projectData.measurementType === 'linear_feet' && ' ($/linear ft)'}
            {projectData.measurementType === 'count' && ' ($/unit)'}
            {' '}- Optional
          </Label>
          <Input 
            id="material-rate"
            type="number"
            placeholder={`Default: $${projectData.materialRate}${
              projectData.measurementType === 'hourly' ? '/hr' :
              projectData.measurementType === 'linear_feet' ? '/ft' :
              projectData.measurementType === 'count' ? '/unit' :
              '/sqft'
            }`}
            value={customMaterialRate}
            onChange={(e) => setCustomMaterialRate(e.target.value)}
          />
        </div>

        {/* Add-ons — inline editor + per-row edit/delete */}
        <InlineAddOnsList
          addOns={addOns}
          setAddOns={setAddOns}
          selectedAddOns={selectedAddOns}
          toggleAddOn={toggleAddOn}
          user={user}
          refreshLibrary={refreshLibrary}
        />
      </CardContent>
    </Card>
  );
};

export default QuoteCalculator;
